import { useEffect, useRef, useState } from 'react';
import {
  ActivityGlyph,
  AttachmentCard,
  Composer,
  MessageAttachmentRow,
  ModePill,
  UserMessage,
} from '~/design-system/components/v1_5/Chat';
import { DeckMark } from '~/design-system/components/v1_5/Header/DeckMark';
import { PanelHeader } from '~/design-system/components/v1_5/Layout';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { PreviewPane } from '~/design-system/components/v1_5/Preview';
import { cn } from '~/lib/utils';
import styles from './ContextPulledDemo.module.css';

/**
 * Feature row 2 — "Your context, pulled from everywhere" (Guri, 2026-09-04).
 *
 * This is the landing's own `FeatureContextDemo` ported, not re-imagined: the same timing constants,
 * the same choreography — **one** activity row at a time, which appears collapsed, holds, expands
 * into what the source found, ticks through it row by row, then dissolves and hands over to the next
 * source. Only the styling is new (dark, v1.5 tokens) and the shell around it: the row lives inside
 * the workspace window.
 *
 * The workspace around that row is SET, not built (Beka, 2026-09-15). The brief with its attachments,
 * every source in the directory and the open document are all there on the first frame and never
 * leave — nothing fades in, and nothing fades out between loops. The one thing that moves is the
 * agent's activity row, because that is the part of the story actually happening.
 *
 * Two deliberate departures from the original, both Guri's standing rules:
 * the in-flight row status is the agent's own `running` mark, never the old CSS spinner, and the
 * thinking indicator stays the three dots.
 */

const THEME = 'dark' as const;
const MODEL = { vendor: 'google', label: 'Gemini 3.8 Flash' } as const;
const ASSETS = '/landing/features/context-demo';

/* ── the reference's timeline, from the brief onward ──────────────────────────────────────── */

const THINKING_MS = 2200;
const COLLAPSED_MS = 1700;
const FIGMA_EXPANDED_MS = 2600;
const ROW_TICK_MS = 650;
const DONE_HOLD_MS = 700;
const SWAP_FADE_MS = 200;
/** The last source holds finished, then hands back to the thinking dots and it begins again. */
const FINAL_HOLD_MS = 2600;

/** No idle or sent beat: the brief is already sent when the reader arrives. */
type Phase = 'thinking' | 'figma' | 'github' | 'linear' | 'notion';

const SOURCE_PHASES = ['figma', 'github', 'linear', 'notion'] as const;
type SourcePhase = (typeof SOURCE_PHASES)[number];

const isSourcePhase = (p: Phase): p is SourcePhase =>
  (SOURCE_PHASES as readonly string[]).includes(p);

interface ListRowData {
  id?: string;
  title: string;
  meta?: string;
}

interface SourceConfig {
  tool: 'figma' | 'github' | 'linear' | 'notion';
  collapsedLabel: string;
  expandedLabel: string;
  rows?: ListRowData[];
  /** Notion rows: one muted title column, no identifier. */
  mutedTitles?: boolean;
}

const SOURCES: Record<SourcePhase, SourceConfig> = {
  figma: {
    tool: 'figma',
    collapsedLabel: 'Seeing what changed since last sprint',
    expandedLabel: 'Figma file: Cart · Payment · Order confirmed',
  },
  github: {
    tool: 'github',
    collapsedLabel: 'Reading through the designs and the code',
    expandedLabel: 'Read 4 commits in deck-ui',
    rows: [
      { id: 'a1f3c92', title: 'Add one-page checkout', meta: 'guri' },
      { id: '7b2e104', title: 'Support Apple Pay and Google Pay', meta: 'beka' },
      { id: 'c93d4a1', title: 'Retry a declined payment', meta: 'guri' },
      { id: '5d8b210', title: 'Send the confirmation email', meta: 'beka' },
    ],
  },
  linear: {
    tool: 'linear',
    collapsedLabel: 'Pulling in what you closed out',
    expandedLabel: 'Reading Linear issues',
    rows: [
      { id: 'SHOP-212', title: 'Redesign the checkout flow' },
      { id: 'SHOP-218', title: 'Cut mobile cart abandonment' },
      { id: 'SHOP-224', title: 'Add express payment options' },
      { id: 'SHOP-231', title: 'Guest checkout, no account' },
    ],
  },
  notion: {
    tool: 'notion',
    collapsedLabel: 'Checking the project documents',
    expandedLabel: 'Reading 4 Notion pages',
    mutedTitles: true,
    rows: [
      { title: 'Checkout redesign PRD' },
      { title: 'Cart abandonment findings' },
      { title: 'Payment provider comparison' },
      { title: 'Launch readiness checklist' },
    ],
  },
};

const FIGMA_TILES = [
  `${ASSETS}/artboard-cart.png`,
  `${ASSETS}/artboard-payment.png`,
  `${ASSETS}/artboard-order-confirmed.png`,
];

const USER_TEXT =
  'Put together a deck on the checkout redesign we just shipped, for the team review Thursday.';

/* ── the directory: grouped by where a thing came from ─────────────────────────────────────── */

/** A picture group draws cards; everything else draws rows. */
interface DirRow {
  title: string;
  meta?: string;
  /** Rows: the 16px glyph in the 30x30 tile. */
  icon?: string;
  brand?: 'figma' | 'github' | 'linear' | 'notion';
  /** Cards: the picture. */
  fill?: string;
}

const GROUPS: readonly { label: string; cards?: boolean; rows: DirRow[] }[] = [
  {
    label: 'Documents',
    rows: [
      // Uploaded, so the extension stays on the name.
      { title: 'research-summary.pdf', meta: 'document · 14 pages', icon: 'file-text' },
      { title: 'rollout-plan.md', meta: 'document · 3 pages', icon: 'file-text' },
      // Written by Deck out of what it read, so no extension.
      { title: 'Checkout redesign PRD', meta: 'document · 8 pages', icon: 'file-text' },
    ],
  },
  {
    label: 'Figma',
    cards: true,
    rows: [
      { title: 'Cart', fill: `url('${ASSETS}/artboard-cart.png') center / cover no-repeat` },
      { title: 'Payment', fill: `url('${ASSETS}/artboard-payment.png') center / cover no-repeat` },
      {
        title: 'Order confirmed',
        fill: `url('${ASSETS}/artboard-order-confirmed.png') center / cover no-repeat`,
      },
    ],
  },
  {
    label: 'GitHub',
    rows: [
      { title: 'Add one-page checkout', meta: 'a1f3c92 · deck-ui', brand: 'github' },
      { title: 'Support Apple and Google Pay', meta: '7b2e104 · deck-ui', brand: 'github' },
    ],
  },
  {
    label: 'Linear',
    rows: [
      { title: 'Redesign the checkout flow', meta: 'SHOP-212 · done', brand: 'linear' },
      { title: 'Cut mobile cart abandonment', meta: 'SHOP-218 · done', brand: 'linear' },
    ],
  },
];

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduce(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

/**
 * The open item is the brief the whole thing was built on, not one of the slides — a real document
 * with a real title, the way the document editor renders one (Guri, 2026-09-04).
 */
function ResearchDoc() {
  return (
    <div className={styles.doc}>
      <div className={styles.docTitle}>Checkout research</div>
      <p className={styles.docPara}>
        Two rounds of moderated testing on the old checkout in June, eight people a round, alongside
        four weeks of funnel data from the same period.
      </p>
      <div className={styles.docHeading}>What the tests said</div>
      <p className={styles.docPara}>
        Everyone who dropped out did it on the same screen. The address form asked for a company name
        before it asked for a street, and six of sixteen people stopped there and never came back.
      </p>
      <p className={styles.docPara}>
        Nobody read the summary panel. It sat right of the fold on every laptop we tested, and not one
        person scrolled to it before paying.
      </p>
      <div className={styles.docHeading}>What we changed</div>
      <p className={styles.docPara}>
        One page instead of four. Express payment first, address second, and the summary moved above
        the button so it is the last thing anyone reads.
      </p>
    </div>
  );
}

export interface ContextPulledDemoProps {
  className?: string;
}

export function ContextPulledDemo({ className }: ContextPulledDemoProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [started, setStarted] = useState(false);

  const [phase, setPhase] = useState<Phase>('thinking');
  const [expanded, setExpanded] = useState(false);
  /** Rows completed in the active list; index === rowsDone is the row being read. */
  const [rowsDone, setRowsDone] = useState(0);
  /** Dissolves the whole activity row, icon included, between sources. */
  const [rowSwapping, setRowSwapping] = useState(false);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStarted(true);
          return;
        }
        // Leaving mid-run would strand whatever the pending timers were about to set — most
        // dangerously `rowSwapping`, which holds the row invisible for the 200ms of a swap. Reset the
        // activity on the way out so coming back always starts from the brief being read.
        setStarted(false);
        setPhase('thinking');
        setExpanded(false);
        setRowsDone(0);
        setRowSwapping(false);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The reference's scripted timeline, from the brief onward.
  useEffect(() => {
    if (!started || reducedMotion) return;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

    // Fade the whole row out, advance, fade the next source in.
    const swapTo = (target: Phase, delay: number) => {
      later(() => setRowSwapping(true), delay);
      later(() => {
        setExpanded(false);
        setRowsDone(0);
        setRowSwapping(false);
        setPhase(target);
      }, delay + SWAP_FADE_MS);
    };

    if (phase === 'thinking') {
      swapTo('figma', THINKING_MS);
    } else if (isSourcePhase(phase)) {
      const i = SOURCE_PHASES.indexOf(phase);
      const isLast = i === SOURCE_PHASES.length - 1;
      if (!expanded) {
        later(() => setExpanded(true), COLLAPSED_MS);
      } else if (phase === 'figma') {
        swapTo(SOURCE_PHASES[i + 1], FIGMA_EXPANDED_MS);
      } else {
        const total = SOURCES[phase].rows?.length ?? 0;
        if (rowsDone < total) {
          later(() => setRowsDone((n) => n + 1), ROW_TICK_MS);
        } else if (isLast) {
          // Round again through the same swap. Only the activity row changes: the brief, the
          // directory and the open document stay exactly where they are.
          swapTo('thinking', FINAL_HOLD_MS);
        } else {
          swapTo(SOURCE_PHASES[i + 1], DONE_HOLD_MS);
        }
      }
    }

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [started, reducedMotion, phase, expanded, rowsDone]);

  const showThinking = phase === 'thinking';
  const activeSource = isSourcePhase(phase) ? SOURCES[phase] : null;
  const listTotal = activeSource?.rows?.length ?? 0;
  const listComplete = rowsDone >= listTotal;

  return (
    <div ref={hostRef} className={cn(styles.window, className)} aria-hidden>
      <div className={styles.bar}>
        <DeckMark theme={THEME} hover="inert" />
        <span className={styles.barTitle}>Deck, checkout review</span>
      </div>
      <div className={styles.panes}>
        <div className={styles.agent}>
          <PanelHeader theme={THEME}>Agent</PanelHeader>
          {/* The shipping pane's own three boxes (DeckAgentPane.tsx): the stage pads 12 and gaps 12,
              the thread's tail adds 8 so its content sits at 20, and the composer keeps the stage's
              12 — which is the 8px step between a bubble's edge and the composer card's. */}
          <div className={styles.stage}>
          <div className={styles.thread}>
            <div className={styles.tail}>
            <div className={styles.userGroup}>
              <UserMessage
                theme={THEME}
                padded={false}
                attachments={
                  <MessageAttachmentRow theme={THEME}>
                    {/* Anything with a picture shows the picture; the rest keep the kind chip. */}
                    <AttachmentCard
                      variant="image"
                      fill={`url('${ASSETS}/attachment-image.png') center / cover no-repeat`}
                      theme={THEME}
                    />
                    <AttachmentCard variant="titled" kind="PDF" name="research-summary" theme={THEME} />
                    <AttachmentCard variant="titled" kind="MD" name="rollout-plan" theme={THEME} />
                  </MessageAttachmentRow>
                }
              >
                {USER_TEXT}
              </UserMessage>
            </div>

            {/* One activity row at a time: the thinking indicator that becomes each source. */}
            <div className={cn(styles.activity, rowSwapping && styles.activitySwapOut)}>
              <div key={phase} className={styles.activityRow}>
                <span className={styles.activityIcon}>
                  {showThinking ? (
                    <span className={styles.typingDots}>
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                    </span>
                  ) : (
                    <ActivityGlyph tool={activeSource?.tool} size={17} theme={THEME} />
                  )}
                </span>

                <div className={styles.activityBody}>
                  <div className={styles.activityHeadline}>
                    <span
                      className={cn(
                        styles.activityLabel,
                        (showThinking || !expanded || !listComplete || phase === 'figma') &&
                          styles.labelShimmer,
                      )}
                    >
                      {showThinking
                        ? 'Reading your notes and metrics'
                        : expanded
                          ? activeSource?.expandedLabel
                          : activeSource?.collapsedLabel}
                    </span>
                    {activeSource ? (
                      <span className={styles.activityChevron}>
                        <ActivityGlyph
                          chrome={expanded ? 'chevron-up' : 'chevron-down'}
                          size={16}
                          theme={THEME}
                        />
                      </span>
                    ) : null}
                  </div>

                  {/* Expand without measuring: 0fr -> 1fr on a grid row. */}
                  {activeSource ? (
                    <div className={cn(styles.reveal, expanded && styles.revealOpen)}>
                      <div className={styles.revealInner}>
                        {phase === 'figma' ? (
                          <div className={styles.tilesRow}>
                            {FIGMA_TILES.map((src, i) => (
                              <span
                                key={src}
                                className={cn(styles.tile, expanded && styles.fadeIn)}
                                style={{
                                  animationDelay: `${i * 70}ms`,
                                  background: `url('${src}') center / cover no-repeat`,
                                }}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className={styles.listBox}>
                            {activeSource.rows?.map((row, i) => {
                              const state =
                                i < rowsDone ? 'done' : i === rowsDone ? 'running' : 'pending';
                              return (
                                <div key={row.title} className={styles.listRow}>
                                  <span className={styles.rowStatus}>
                                    <ActivityGlyph state={state} size={14} theme={THEME} />
                                  </span>
                                  {row.id ? (
                                    <span
                                      className={cn(
                                        styles.rowId,
                                        state === 'running' && styles.rowShimmer,
                                      )}
                                    >
                                      {row.id}
                                    </span>
                                  ) : null}
                                  <span
                                    className={cn(
                                      styles.rowTitle,
                                      activeSource.mutedTitles && styles.rowTitleMuted,
                                      state === 'running' && styles.rowShimmer,
                                    )}
                                  >
                                    {row.title}
                                  </span>
                                  {row.meta ? <span className={styles.rowMeta}>{row.meta}</span> : null}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            </div>
          </div>
          <div className={styles.composerWrap}>
            <Composer
              theme={THEME}
              input={{ content: 'placeholder', text: 'Ask, reply anything' }}
              toolbar={{
                model: MODEL,
                // Web search is on, and the lit globe alone says so — the label would push the
                // toolbar past the 380px pane (Guri, 2026-09-04).
                modes: <ModePill mode="search" label="" theme={THEME} className={styles.globeOnly} />,
                // The agent is at work on every frame of this row, so the button is always Stop.
                send: { state: 'stop' },
              }}
            />
          </div>
          </div>
        </div>

        {/* Every kind at once — what a real deck's directory holds — all of it there from the start. */}
        <div className={styles.directory}>
          <PanelHeader theme={THEME}>All sources</PanelHeader>
          <div className={styles.list}>
            {GROUPS.map((group) => (
              <div key={group.label} className={styles.section}>
                <div className={styles.sectionLabel}>{group.label}</div>
                {group.cards ? (
                  <div className={styles.cardGrid}>
                    {group.rows.map((row) => (
                      <div key={row.title} className={styles.card}>
                        <span className={styles.cardPic} style={{ background: row.fill }} />
                        <span className={styles.cardTitle}>{row.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  group.rows.map((row) => (
                    <div
                      key={row.title}
                      className={cn(styles.row, row.title === 'research-summary.pdf' && styles.rowSelected)}
                    >
                      <span className={styles.tile}>
                        {row.brand ? (
                          <ActivityGlyph tool={row.brand} size={16} theme={THEME} />
                        ) : (
                          <DeckIcon name={row.icon ?? 'file-text'} size={16} />
                        )}
                      </span>
                      <span className={styles.text}>
                        <span className={styles.rowTitleText}>{row.title}</span>
                        <span className={styles.rowMetaText}>{row.meta}</span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.preview}>
          {/* Open from the first frame: it is the row selected in the directory beside it. */}
          <PreviewPane
            theme={THEME}
            title="Checkout research"
            meta="document · 14 pages"
            status="ready"
            kind="document"
            showZoom={false}
          >
            <ResearchDoc />
          </PreviewPane>
        </div>
      </div>
    </div>
  );
}

ContextPulledDemo.displayName = 'ContextPulledDemo';
